import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import { useAppSelector } from 'app/store'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import SimpleLink from 'core/components/SimpleLink'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { portValidator, validIpv4Validator } from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import PicklistField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Text from 'pf9-ui-components/built/elements/Text'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import { addMemberToPool, listOctaviaPoolMembers, listOctaviaPools, listSubnets } from './actions'
import { getMemberRequestBody } from './helpers'
import { ILoadBalancerDetailsPageTabs } from './model'
import PoolsPicklist from './network-details/PoolsPicklist'
import SubnetPicklist from './network-details/SubnetPicklist'
import RouterOptionsPicklist from './RouterOptionsPicklist'
import { listRouters } from './routers/actions'
import { routersSelector } from './routers/selectors'
import { loadBalancersSelector, octaviaPoolsSelector, subnetsByIdSelector } from './selectors'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import { loadBalancerDoc } from 'app/docLinks'

const { neutron } = ApiClient.getInstance()

const useStyles = makeStyles<Theme>((theme) => ({
  formFieldSection: {
    marginBottom: '32px',
    border: 'none',
    padding: 0,
  },
  pageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    display: 'grid',
    gap: 16,
    gridAutoRows: 'max-content',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  iconWrapper: {
    display: 'flex',
    height: '32px',
    width: '32px',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customFormFieldSection: {
    '& .content': {
      marginLeft: '0px',
    },
  },
  noRouterAlert: {
    display: 'grid',
    gridAutoFlow: 'columns',
    gap: theme.spacing(1),
  },
}))

interface Props {
  addRoute: Route
}

export default function CreateMemberModal({ addRoute }: Props) {
  const { match, history } = useReactRouter()
  const imagePath = '/ui/images/add-member-logo.svg'
  const classes = useStyles()
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const defaultParams = {
    name: '',
    pool: '',
    vipSubnet: null,
    ipAddress: '',
    port: '',
    provider: 'ovn',
    router: '',
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const [availableRouters, setAvailableRouters] = useState([])
  const [isDifferentSubnet, setIsDifferentSubnet] = useState(false)
  const [checkingRouter, setCheckingRouter] = useState(false)
  const [showRouterCreationDialog, setShowRouterCreationDialog] = useState(false)

  const { reload } = useListAction(listOctaviaPoolMembers, {})
  const { update, updating, error, reset } = useUpdateAction(addMemberToPool)
  useListAction(listOctaviaPools)
  const pools = useAppSelector(octaviaPoolsSelector)
  const loadBalancers = useAppSelector(loadBalancersSelector)

  // Get routers
  useListAction(listRouters)
  const routers = useAppSelector(routersSelector)

  // Get subnets by id
  useListAction(listSubnets)
  const subnetsById = useAppSelector(subnetsByIdSelector)

  const tenantRouters = useMemo(() => {
    return routers?.filter((router) => {
      return router.project_id === currentTenant
    })
  }, [routers, currentTenant])

  const isDisabled = pools.length === 0

  const createListenerFn = async (params) => {
    const body = getMemberRequestBody(params)
    const { success, response } = await update({ poolId: params.pool, body })
    return { success, response }
  }

  // Get load balancers of selected pool
  const loadBalancersOfSelectedPool = useMemo(
    () => pools?.find((pool) => pool.id === params.pool)?.loadbalancers?.flatMap((lb) => lb?.id),
    [pools, params.pool],
  )

  // Get load balancer subnets of selected pool
  const loadBalancerSubnets = useMemo(() => {
    return loadBalancers
      ?.filter((lb) => loadBalancersOfSelectedPool?.includes(lb.id))
      ?.flatMap((lb) => lb?.vip_subnet_id)
  }, [loadBalancers, loadBalancersOfSelectedPool])

  // Get common router for subnets if vip subnet is different from load balancer subnet
  const getCommonRouterForSubnets = useCallback(
    async (isDifferentSubnet) => {
      try {
        setCheckingRouter(true)
        setAvailableRouters([])
        const ports = await neutron.getPortsForSubnets({
          subnetIds: isDifferentSubnet
            ? [params.vipSubnet?.id, ...loadBalancerSubnets]
            : [params.vipSubnet?.id],
        })

        // If subnets are different
        // AND IF Ports length is less than or equal to 1 which means that there are no ports for each subnets
        // OR If all ports have different device_id which means that there are no common router for each subnets
        // OR If vip subnet is same as load balancer subnet and there are no ports it means that there is no router available for this subnet
        if (
          isDifferentSubnet
            ? ports?.length <= 1 ||
              (ports?.[0]?.device_id &&
                ports?.some((port) => port?.device_id !== ports?.[0]?.device_id))
            : !ports.length
        ) {
          setParams({ ...params, router: '' })
          return
        }

        const router = tenantRouters?.find((router) => router.id === ports?.[0]?.device_id)
        if (!router) {
          setParams({ ...params, router: '' })
          return
        }

        setAvailableRouters([router])
        setParams({ ...params, router: router.id })
      } catch (e) {
        setParams({ ...params, router: '' })
      } finally {
        setCheckingRouter(false)
      }
    },
    [params?.vipSubnet, loadBalancerSubnets, tenantRouters],
  )

  // useEffect to check if the vip subnet is same as the load balancer subnet
  // If No, check if there is a common router for both subnets
  // If Yes, check if the vip subnet have a router for the load balancer subnet
  useEffect(() => {
    if (!params?.vipSubnet) {
      setIsDifferentSubnet(false)
      return
    }

    const isDifferentSubnet =
      !!params?.pool &&
      !!params.vipSubnet?.id &&
      !!loadBalancerSubnets.length &&
      !loadBalancerSubnets?.includes(params.vipSubnet?.id)

    setIsDifferentSubnet(isDifferentSubnet)
    getCommonRouterForSubnets(isDifferentSubnet)
  }, [params?.vipSubnet, loadBalancerSubnets, params?.pool, getCommonRouterForSubnets])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(
      routeManager.routes.openstack.loadBalancers.details.path({
        tab: ILoadBalancerDetailsPageTabs.Members,
      }),
    )
  }

  const submitForm = useCallback(async () => {
    setAttemptedSubmit(true)

    const { success } = await createListenerFn(params)
    if (success) {
      handleClose()
      reload()
    }
    setAttemptedSubmit(false)
  }, [params, createListenerFn, handleClose, reload])

  const redirectLink = () => {
    history.push(
      routeManager.routes.openstack.createLoadBalancers.path({
        tab: ILoadBalancerDetailsPageTabs.Pools,
      }),
    )
  }

  const ceateRouterLink = useCallback(() => {
    history.push(
      routeManager.routes.openstack.createRouter.path(
        isDifferentSubnet
          ? {
              fromLoadBalanceMember: true,
            }
          : {},
      ),
    )
  }, [isDifferentSubnet])

  return (
    <ModalForm
      route={addRoute}
      title={`Add Member`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || attemptedSubmit}
      error={error}
      submitTitle={`Add Member`}
      maxWidth={470}
      disableSubmit={isDisabled}
    >
      <>
        <div className={classes.formFieldSection}>
          {isDisabled && (
            <div className={classes.formFieldSection}>
              <Alert title="No Pool Detected" variant="error">
                <Text variant="body2">
                  In order to create a member, you first need to create a pool.
                </Text>
                <SimpleLink onClick={redirectLink}>Create Pool</SimpleLink>
              </Alert>
            </div>
          )}
          <div className={classes.contentContainer}>
            <img
              className={classes.iconWrapper}
              alt="Member"
              src={imagePath}
              data-testid="create-member-modal--img-0"
            />
            <div className={classes.content}>
              <Text variant="body2">
                Add a <ExternalLink url={loadBalancerDoc}>member</ExternalLink> (Virtual Machines)
                to the pool that will receive client requests.
              </Text>
            </div>
          </div>
        </div>
        <FormFieldSection className={classes.customFormFieldSection} title="Member Configuration">
          <TextField
            id="name"
            label="Member Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            disabled={isDisabled}
            required
            enterKeyHint={undefined}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            crossOrigin={undefined}
          />
          <PicklistField
            DropdownComponent={PoolsPicklist}
            name="pool"
            id={`fixedIps.{idx}.pool`}
            label="Pool"
            compact={false}
            onChange={getParamsUpdater('pool')}
            info="Pool that will handle the client requests from the load balancer."
            disabled={isDisabled}
            required
          />
          <PicklistField
            DropdownComponent={SubnetPicklist}
            name="subnet"
            id={`fixedIps.{idx}.subnet`}
            label="Subnet"
            compact={false}
            onChange={getParamsUpdater('vipSubnet')}
            info="This subnet should be the same tenant network where your backend servers (Virtual Machines) are deployed."
            disabled={isDisabled}
            required
          />
          {isDifferentSubnet ? (
            <Text variant="body2">
              <b>NOTE:</b> For this member, you selected a different subnet than the one assigned to
              the load balancer. As a result, you need to add a router; otherwise, the load balancer
              will be unreachable.
            </Text>
          ) : null}
          {params?.vipSubnet ? (
            <>
              {availableRouters?.length > 0 || checkingRouter ? (
                <PicklistField
                  DropdownComponent={RouterOptionsPicklist}
                  name="router"
                  id={`router`}
                  label="Router"
                  onChange={getParamsUpdater('router')}
                  info="Router that will handle the client requests from the load balancer."
                  disabled={isDisabled}
                  routers={availableRouters}
                  loading={checkingRouter}
                  required
                />
              ) : (
                <>
                  <ConfirmationDialog
                    open={showRouterCreationDialog}
                    title="Are you sure?"
                    text="You are temporarily exiting the member creation process in order to create a router that fulfills the specified subnet requirements. After the router has been created, return to this step and associate it with the member"
                    onConfirm={ceateRouterLink}
                    onCancel={() => setShowRouterCreationDialog(false)}
                    confirmText="Go to Router Creation page"
                    cancelText="Cancel"
                  />
                  <Alert
                    title="No Router Detected"
                    message={
                      <div className={classes.noRouterAlert}>
                        <Text variant="body2">
                          {isDifferentSubnet
                            ? 'A router capable of supporting the specified two subnets could not be found. Therefore, a new router needs to be created to accommodate both subnets.'
                            : 'A router capable of supporting the specified subnet could not be found. Therefore, a new router needs to be created to accommodate the subnet.'}
                        </Text>
                        <div>
                          <Text variant="body2">
                            Load Balancer Subnet: <b>{subnetsById[loadBalancerSubnets[0]]?.name}</b>
                          </Text>
                          {isDifferentSubnet && (
                            <Text variant="body2">
                              Member Subnet: <b>{params.vipSubnet?.name}</b>
                            </Text>
                          )}
                        </div>
                        <SimpleLink onClick={() => setShowRouterCreationDialog(true)}>
                          <b>Create Router</b>
                        </SimpleLink>
                      </div>
                    }
                    variant="error"
                  />
                </>
              )}
            </>
          ) : null}
          <TextField
            id="ipAddress"
            label="IP Address"
            onChange={getParamsUpdater('ipAddress')}
            value={params.ipAddress}
            placeholder="Enter IP Address (eg. 192.168.2.100)"
            info="Virtual machines that will receive the client requests."
            disabled={isDisabled}
            validations={[validIpv4Validator]}
            required
            enterKeyHint={undefined}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            crossOrigin={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
          <TextField
            id="port"
            label="Port"
            onChange={getParamsUpdater('port')}
            value={params.port}
            placeholder="Enter Port (eg. 80)"
            info="The port on which the backend server (Virtual machine) is listening."
            type="number"
            disabled={isDisabled}
            validations={[portValidator]}
            required
            enterKeyHint={undefined}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            crossOrigin={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
